'use client';

import { useLanguage } from '@/app/context/language-context';
import { motion } from 'framer-motion';
import { ShieldCheck, TrendingUp, Clock, Trophy } from 'lucide-react';

// Animation variants
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

export default function WhyChooseUsSection() {
  const { t } = useLanguage();
  const features = [
    {
      icon: (
        <TrendingUp className='w-10 h-10 text-primarycolor dark:text-secondarycolor' />
      ),
      title: t('marketExpertise'),
      description: t('marketExpertiseDescription'),
    },
    {
      icon: (
        <ShieldCheck className='w-10 h-10 text-primarycolor dark:text-secondarycolor' />
      ),
      title: t('trustedAdvisors'),
      description: t('trustedAdvisorsDescription'),
    },
    {
      icon: (
        <Clock className='w-10 h-10 text-primarycolor dark:text-secondarycolor' />
      ),
      title: t('timeEfficiency'),
      description: t('timeEfficiencyDescription'),
    },
    {
      icon: (
        <Trophy className='w-10 h-10 text-primarycolor dark:text-secondarycolor' />
      ),
      title: t('awardWinningService'),
      description: t('awardWinningServiceDescription'),
    },
  ];

  return (
    <motion.section
      className='py-10 px-4 bg-gray-50 dark:bg-gray-800'
      initial='hidden'
      whileInView='visible'
      viewport={{ once: true, margin: '-100px' }}
      variants={staggerContainer}
    >
      <div className='max-w-7xl mx-auto'>
        <motion.div className='text-center mb-12' variants={fadeIn}>
          <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
            {t('whyChooseUs')}
          </h2>
          <p className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'>
            {t('whyChooseUsDescription')}
          </p>
        </motion.div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className='bg-gray-50 dark:bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow'
              variants={cardVariants}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <div className='rounded-full bg-white dark:bg-gray-700 p-4 inline-block mb-4'>
                {feature.icon}
              </div>
              <h3 className='text-xl font-semibold text-gray-900 dark:text-white mb-3'>
                {feature.title}
              </h3>
              <p className='text-gray-600 dark:text-gray-400'>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
